/**
 * Preferences — per `rcp-preferences.html`.
 *
 * A11y fixes (mockup #11):
 *   - Switches: `role="switch"` + `aria-checked`.
 *   - Grouped via `<fieldset>` + `<legend>`.
 *   - Per-channel / per-category consequence text is rendered inline.
 */

import { useEffect, useState } from 'react';
import type { CustomerPreferencesDto } from '@retail-ops/shared-types';
import { getApiClient } from '../../services/apiClient';
import { useToast } from '../../components/a11y/AccessibleToast';

type PreferencesDraft = Pick<
  CustomerPreferencesDto,
  'emailOptIn' | 'smsOptIn' | 'marketingOptIn' | 'remindersOptIn'
>;

const CONSEQUENCES: Record<keyof PreferencesDraft, string> = {
  emailOptIn:     'You won&apos;t get any email from us, including important account notices — we&apos;ll mail paper copies instead.',
  smsOptIn:       'We won&apos;t send text messages, including security alerts.',
  marketingOptIn: 'You will still receive transactional and security messages. This only controls offers and promotions.',
  remindersOptIn: 'You may miss helpful reminders like appointment confirmations.',
};

export function Preferences(): JSX.Element {
  const { pushToast } = useToast();
  const [draft, setDraft] = useState<PreferencesDraft>({
    emailOptIn: true,
    smsOptIn: false,
    marketingOptIn: false,
    remindersOptIn: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        // TODO(PLANNED /portal/preferences): server returns CustomerPreferencesDto.
        const res = await getApiClient().portal.preferences.get({ signal: controller.signal });
        if (res.success && res.data) {
          const { emailOptIn, smsOptIn, marketingOptIn, remindersOptIn } = res.data;
          setDraft({ emailOptIn, smsOptIn, marketingOptIn, remindersOptIn });
        }
      } catch {
        /* keep defaults */
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  function toggle<K extends keyof PreferencesDraft>(key: K): void {
    setDraft((cur) => ({ ...cur, [key]: !cur[key] }));
  }

  async function save(): Promise<void> {
    setSaving(true);
    try {
      // TODO(PLANNED /portal/preferences PATCH).
      const res = await getApiClient().portal.preferences.update(draft);
      if (res.success) {
        pushToast('success', 'Preferences saved');
      } else {
        pushToast('error', 'Could not save preferences', res.error ?? 'Please try again.');
      }
    } catch {
      pushToast('error', 'Network error', 'Please check your connection.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p aria-live="polite">Loading preferences...</p>;

  return (
    <section aria-labelledby="prefs-heading">
      <h1 id="prefs-heading" tabIndex={-1}>Notification preferences</h1>
      <p className="lead">Choose how we reach you.</p>

      <fieldset className="card" style={{ border: '1px solid var(--surface-border)' }}>
        <legend style={{ padding: '0 8px', fontWeight: 500, color: 'var(--pcb-blue-dark)' }}>
          Channels
        </legend>
        <ToggleRow
          label="Email"
          hint="Transactional notices, receipts, and bank alerts."
          consequence={CONSEQUENCES.emailOptIn}
          checked={draft.emailOptIn}
          onToggle={() => toggle('emailOptIn')}
        />
        <ToggleRow
          label="Text message (SMS)"
          hint="Time-sensitive alerts like fraud detection and OTP backups."
          consequence={CONSEQUENCES.smsOptIn}
          checked={draft.smsOptIn}
          onToggle={() => toggle('smsOptIn')}
        />
      </fieldset>

      <fieldset className="card" style={{ border: '1px solid var(--surface-border)' }}>
        <legend style={{ padding: '0 8px', fontWeight: 500, color: 'var(--pcb-blue-dark)' }}>
          Categories
        </legend>
        <ToggleRow
          label="Offers and promotions"
          hint="New products, rate specials, and events."
          consequence={CONSEQUENCES.marketingOptIn}
          checked={draft.marketingOptIn}
          onToggle={() => toggle('marketingOptIn')}
        />
        <ToggleRow
          label="Reminders"
          hint="Appointment reminders and helpful nudges."
          consequence={CONSEQUENCES.remindersOptIn}
          checked={draft.remindersOptIn}
          onToggle={() => toggle('remindersOptIn')}
        />
      </fieldset>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void save()}
          disabled={saving}
          aria-busy={saving || undefined}
        >
          {saving ? 'Saving...' : 'Save preferences'}
        </button>
      </div>
    </section>
  );
}

function ToggleRow({
  label,
  hint,
  consequence,
  checked,
  onToggle,
}: {
  label: string;
  hint: string;
  consequence: string;
  checked: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        padding: '16px 0',
        borderBottom: '1px solid var(--surface-border)',
        gap: 16,
      }}
    >
      <div>
        <div style={{ fontWeight: 500, color: 'var(--pcb-blue-dark)' }}>{label}</div>
        <div style={{ fontSize: 13, color: 'var(--ink-500)' }}>{hint}</div>
        {!checked ? (
          <div style={{ fontSize: 12, color: 'var(--warn-text)', marginTop: 4 }}
               dangerouslySetInnerHTML={{ __html: consequence.replace(/&apos;/g, '’') }} />
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={`${label}: ${checked ? 'on' : 'off'}`}
        onClick={onToggle}
        style={{
          width: 48,
          height: 26,
          borderRadius: 999,
          background: checked ? 'var(--pcb-green)' : 'var(--ink-400)',
          position: 'relative',
          cursor: 'pointer',
          border: 'none',
          transition: 'background 0.15s',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 3,
            left: checked ? 25 : 3,
            width: 20,
            height: 20,
            background: '#fff',
            borderRadius: '50%',
            transition: 'left 0.15s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      </button>
    </div>
  );
}
